/**
 * A avaliação da formalização na tela (E152.F3, ADR-0103 §5.9): lê ao montar, a cada `releitura`
 * (o caso relido depois do exame) e a pedido, e registra a revisão do perito.
 *
 * Releitura com a avaliação já na tela não volta a "carregando": o painel continua montado e o
 * rascunho da revisão fica. Quando o servidor diz que a versão mudou, a tela relê em silêncio e
 * avisa; o texto do perito continua no campo.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';

import {
  AvaliacaoDaFormalizacaoError,
  enviarRevisaoDoPerito,
  lerAvaliacaoDaFormalizacao,
  type MotivoDaFalhaDaAvaliacao,
  type PainelDaFormalizacaoData,
  type RevisaoInData,
} from '@/lib/formalizacaoAvaliacao';

export type EstadoDaAvaliacao =
  | { estado: 'carregando' }
  | { estado: 'erro'; motivo: MotivoDaFalhaDaAvaliacao }
  | { estado: 'pronto'; dados: PainelDaFormalizacaoData };

export type EnvioDaRevisao =
  | { estado: 'ocioso' }
  | { estado: 'enviando'; objeto: string }
  | { estado: 'registrada'; objeto: string }
  | { estado: 'recusada'; objeto: string; motivo: MotivoDaFalhaDaAvaliacao };

function motivo(err: unknown): MotivoDaFalhaDaAvaliacao {
  return err instanceof AvaliacaoDaFormalizacaoError ? err.motivo : 'indisponivel';
}

export function useAvaliacaoDaFormalizacao(
  client: AxiosInstance,
  caseId: string,
  releitura: unknown,
): {
  avaliacao: EstadoDaAvaliacao;
  envio: EnvioDaRevisao;
  reler: () => void;
  revisar: (revisao: RevisaoInData) => Promise<boolean>;
} {
  const [avaliacao, setAvaliacao] = useState<EstadoDaAvaliacao>({ estado: 'carregando' });
  const [envio, setEnvio] = useState<EnvioDaRevisao>({ estado: 'ocioso' });
  const [pedido, setPedido] = useState(0);
  const reler = useCallback(() => setPedido((n) => n + 1), []);

  useEffect(() => {
    let vivo = true;
    setAvaliacao((atual) => (atual.estado === 'pronto' ? atual : { estado: 'carregando' }));
    void (async () => {
      try {
        const dados = await lerAvaliacaoDaFormalizacao(client, caseId);
        if (vivo) setAvaliacao({ estado: 'pronto', dados });
      } catch (err) {
        if (vivo) setAvaliacao({ estado: 'erro', motivo: motivo(err) });
      }
    })();
    return () => {
      vivo = false;
    };
  }, [client, caseId, releitura, pedido]);

  const revisar = useCallback(
    async (revisao: RevisaoInData): Promise<boolean> => {
      setEnvio({ estado: 'enviando', objeto: revisao.objeto });
      try {
        const dados = await enviarRevisaoDoPerito(client, caseId, revisao);
        setAvaliacao({ estado: 'pronto', dados });
        setEnvio({ estado: 'registrada', objeto: revisao.objeto });
        return true;
      } catch (err) {
        setEnvio({ estado: 'recusada', objeto: revisao.objeto, motivo: motivo(err) });
        if (motivo(err) === 'desatualizada') reler();
        return false;
      }
    },
    [client, caseId, reler],
  );

  return { avaliacao, envio, reler, revisar };
}
